import React, { useEffect, useState, useMemo } from 'react';
import { Checkbox, Divider, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getReportFormat, getTodaysReport } from '../../services/transactions';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import moment from 'moment';
import useStyles from './styles';
import LoadingScreen from '../../utils/LoadingScreen';

const ExportReports = () => {
  const [selectedStockist, setSelectedStockist] = useState(null);
  const [selectedStockistName, setSelectedStockistName] = useState('');
  const [selectedStockistCode, setSelectedStockistCode] = useState('');
  const stokistName = selectedStockistName?.replace(/\./g, '') || 'Stockist';
  const stokistCode = selectedStockistCode?.replace(/\./g, '') || 'Code';
  const fileName = `${stokistName}_${stokistCode}`;
  const count = useSelector((state) => state.scanResult.count);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stockists, setStockists] = useState([]);
  const [records, setRecords] = useState([]);
  const [fromDate, setFromDate] = useState(moment().startOf('day'));
  const [toDate, setToDate] = useState(moment().endOf('day'));
  const [selectedCategoryCheckboxes, setSelectedCategoryCheckboxes] = useState({});
  const [selectedClaimNoCheckboxes, setSelectedClaimNoCheckboxes] = useState({});
  const [selectedLrNoCheckboxes, setSelectedLrNoCheckboxes] = useState({});
  const [selectedRemarkCheckboxes, setSelectedRemarkCheckboxes] = useState({});

  const loadReportType = async () => {
    try {
      const response = await getReportFormat();
      if (response['status'] === 'success') {
        setHeaders(
          response['data']['columns']
            ? JSON.parse(response['data']['columns'])
            : [
              { key: 'type', label: 'Type' },
              { key: 'product.SKU', label: 'Product Name' },
              { key: 'product.BATCHNUMBER', label: 'Batch Number' },
              { key: 'product.MRP', label: 'MRP' },
              { key: 'quantity', label: 'Quantity' },
              { key: 'formattedExpiryDate', label: 'Expiry Date' },
              { key: 'formattedMFGDate', label: 'Mfg Date' },
              { key: 'product.category', label: 'Category' },
              { key: 'product.remark', label: 'Remark' },
              { key: 'claimNo', label: 'Claim No.' },
            ]
        );
      } else {
        toast.error('Error while getting the report format. Please contact ORBO');
      }
    } catch (error) {
      toast.error('Error while getting the report format. Please try again.');
    }
  };

  useEffect(() => {
    loadReportType();
  }, []);

  const loadTodaysReport = async () => {
    if (toDate.diff(fromDate, 'hours') <= 0) {
      toast.error('To Date must be greater than From Date');
      return;
    }
    setLoading(true);
    try {
      const response = await getTodaysReport(
        fromDate.format('DD/MM/YYYY'),
        toDate.format('DD/MM/YYYY')
      );
      if (response['status'] === 'success') {
        const responsedata = response['data'];
        const data = responsedata.filter(item => {
          const quantity = item.quantity;
          return quantity !== 0 &&
            quantity !== null &&
            quantity !== undefined &&
            quantity !== '' &&
            quantity !== 'None' &&
            quantity !== '0';
        });
        // console.log('Report data:', data);
        let tmpStockists = [];
        const sortedItems = [...data].sort((a, b) => a.platform.localeCompare(b.platform));

        const stockistMap = new Map();

        for (const entry of sortedItems) {
          if (entry.product != null) {
            const product = entry.product;
            const newEntry = { ...entry };
            newEntry['formattedExpiryDate'] = moment(
              product['EXPIRYDATE'],
              'YYYYMMDD'
            )
              .endOf('month')
              .format('DD/MM/YYYY');

            const mfgDate = product?.MFG_DATE;
            newEntry['formattedMFGDate'] =
              !mfgDate ||
                mfgDate === 'nan' ||
                mfgDate === 'Invalid date' ||
                mfgDate === '-NA-'
                ? ''
                : moment(mfgDate, 'YYYYMMDD', true).isValid()
                  ? moment(mfgDate, 'YYYYMMDD').format('DD/MM/YYYY')
                  : '';
            newEntry['platform'] = newEntry['platform'] === 'web' ? 'Manual' : newEntry['platform'];
            newEntry['date'] = moment(entry['date']).local().format('DD/MM/YYYY HH:mm:ss');
            newEntry['Username'] = entry.userDetails?.name || '';
            newEntry.product.remark = entry.remark || '';
            newEntry.product.category = product.category || entry.category;

            if (sessionStorage.getItem('reportType') === 'fdc-online') {
              const recDate = newEntry.lrDate
                ? moment(newEntry.lrDate, "DD/MM/YYYY")
                : moment(newEntry.date, "DD/MM/YYYY HH:mm:ss");
              const expiryDate = moment(product.EXPIRYDATE, 'YYYYMMDD').endOf('month');
              const currentMonth = moment(recDate, 'DD/MM/YYYY').month() + 1;
              const currentYear = moment(recDate, 'DD/MM/YYYY').year();
              const expiryDateMonth = expiryDate.month() + 1;
              const expiryDateYear = expiryDate.year();

              if (
                expiryDateYear < currentYear ||
                (expiryDateYear === currentYear && expiryDateMonth <= currentMonth)
              ) {
                newEntry.product.category = 'FG02';
              } else {
                const daysDifference = expiryDate.diff(moment(recDate, 'DD/MM/YYYY'), 'days');
                newEntry.product.category = daysDifference <= 120 && daysDifference > 0 ? 'NEXP' : 'DAMG';
              }
            }

            const stockistCode = entry.associatedWith;
            if (!stockistMap.has(stockistCode)) {
              stockistMap.set(stockistCode, {
                code: stockistCode,
                name: entry.associateDetails ? entry.associateDetails.name : stockistCode,
                records: [],
                recordMap: new Map() // To track products within this stockist
              });
            }

            const stockist = stockistMap.get(stockistCode);
            newEntry.quantity = Number(entry.quantity);

            const normalize = (val) => String(val || '').trim().toLowerCase();
            const productKey = [
              normalize(newEntry.type),
              normalize(newEntry.product.ITEM_CODE),
              normalize(newEntry.product.SKU),
              normalize(newEntry.product.BATCHNUMBER),
              normalize(newEntry.formattedExpiryDate),
              normalize(newEntry.product.MRP),
              normalize(newEntry.product.remark),
              normalize(newEntry.product.category),
              normalize(newEntry.claimNo),
              normalize(newEntry.lrNo)
            ].join('|');

            if (!stockist.recordMap.has(productKey)) {
              stockist.records.push(newEntry);
              stockist.recordMap.set(productKey, stockist.records.length - 1);
            } else {
              const productIndex = stockist.recordMap.get(productKey);
              stockist.records[productIndex].quantity += Number(entry.quantity);
            }
          }
        }

        tmpStockists = Array.from(stockistMap.values())
          .map(({ recordMap, ...rest }) => {
            // Sort records within each stockist by product name (SKU)
            rest.records.sort((a, b) => (a.product?.SKU || '').localeCompare(b.product?.SKU || ''));
            return rest;
          })
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        setStockists(tmpStockists);
        if (tmpStockists.length > 0) {
          setSelectedStockist(tmpStockists[0]?.code);
          setRecords(tmpStockists[0].records);
          setSelectedStockistCode(tmpStockists[0]?.code);
          setSelectedStockistName(tmpStockists[0]?.name);
        }
      } else {
        toast.error(response['error'] || 'Error loading report');
      }
    } catch (error) {
      toast.error('Error loading report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSelectStockist = (code) => {
    setSelectedStockist(code);
    const index = stockists.findIndex((item) => item.code === code);
    let tempRecords = stockists[index].records;
    if (sessionStorage.getItem('reportType') === 'fdc-online') {
      tempRecords = tempRecords.map((record) => {
        const recDate = record.lrDate
          ? moment(record.lrDate, "DD/MM/YYYY").format("DD/MM/YYYY")
          : moment(record.date, "DD/MM/YYYY HH:mm:ss").format("DD/MM/YYYY");

        const expiryDate = moment(record.product.EXPIRYDATE, 'YYYYMMDD').endOf('month');
        const currentMonth = moment(recDate, 'DD/MM/YYYY').month() + 1;
        const currentYear = moment(recDate, 'DD/MM/YYYY').year();
        const expiryDateMonth = expiryDate.month() + 1;
        const expiryDateYear = expiryDate.year();

        let categoryValue = record.product.category;
        if (
          expiryDateYear < currentYear ||
          (expiryDateYear === currentYear && expiryDateMonth <= currentMonth)
        ) {
          categoryValue = 'FG02';
        } else {
          const daysDifference = expiryDate.diff(moment(recDate, 'DD/MM/YYYY'), 'days');
          categoryValue = daysDifference <= 120 && daysDifference > 0 ? 'NEXP' : 'DAMG';
        }
        return { ...record, product: { ...record.product, category: categoryValue } };
      });
    }
    setRecords(tempRecords);
    setSelectedStockistCode(stockists[index]?.code);
    setSelectedStockistName(stockists[index]?.name);
  };

  useEffect(() => {
    setSelectedCategoryCheckboxes(getCategoryCSVLinks());
    setSelectedClaimNoCheckboxes(getClaimNoCSVLinks());
    setSelectedLrNoCheckboxes(getLrNoCSVLinks());
    setSelectedRemarkCheckboxes(getRemarkCSVLinks());
  }, [records]);

  const filteredRecords = useMemo(() => {
    const selectedCategories = Object.keys(selectedCategoryCheckboxes).filter(
      category => selectedCategoryCheckboxes[category]
    );

    const selectedClaimNos = Object.keys(selectedClaimNoCheckboxes).filter(
      claimNo => selectedClaimNoCheckboxes[claimNo]
    );
    const selectedLrNos = Object.keys(selectedLrNoCheckboxes).filter(
      lrNo => selectedLrNoCheckboxes[lrNo]
    );
    const selectedRemarks = Object.keys(selectedRemarkCheckboxes).filter(
      remark => selectedRemarkCheckboxes[remark]
    );

    let tempFilteredRecords = records;

    if (selectedCategories.length > 0) {
      tempFilteredRecords = tempFilteredRecords.filter(record =>
        selectedCategories.includes(record?.product?.category || 'Uncategorized')
      );
    }

    if (selectedClaimNos.length > 0) {
      tempFilteredRecords = tempFilteredRecords.filter(record =>
        selectedClaimNos.includes(record?.claimNo || 'Uncategorized')
      );
    }

    if (selectedLrNos.length > 0) {
      tempFilteredRecords = tempFilteredRecords.filter(record =>
        selectedLrNos.includes(record?.lrNo || 'Uncategorized')
      );
    }

    if (selectedRemarks.length > 0) {
      tempFilteredRecords = tempFilteredRecords.filter(record =>
        selectedRemarks.includes(record?.product?.remark || 'Uncategorized')
      );
    }

    return tempFilteredRecords;
  }, [records, selectedCategoryCheckboxes, selectedClaimNoCheckboxes, selectedLrNoCheckboxes, selectedRemarkCheckboxes]);

  const filteredRecordsCount = filteredRecords.length;

  const groupedByCategory = useMemo(() => {
    const grouped = {};
    records.forEach((record) => {
      const category = record?.product?.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(record);
    });
    return grouped;
  }, [records]);

  const groupedByClaimNo = useMemo(() => {
    const grouped = {};
    filteredRecords.forEach((record) => {
      const claimNo = record?.claimNo || 'Uncategorized';
      if (!grouped[claimNo]) {
        grouped[claimNo] = [];
      }
      grouped[claimNo].push(record);
    });
    return grouped;
  }, [filteredRecords]);

  const groupedByLrNo = useMemo(() => {
    const grouped = {};
    filteredRecords.forEach((record) => {
      const lrNo = record?.lrNo || 'Uncategorized';
      if (!grouped[lrNo]) {
        grouped[lrNo] = [];
      }
      grouped[lrNo].push(record);
    });
    return grouped;
  }, [filteredRecords]);

  const groupedByRemark = useMemo(() => {
    const grouped = {};
    filteredRecords.forEach((record) => {
      const remark = record?.product?.remark || 'Uncategorized';
      if (!grouped[remark]) {
        grouped[remark] = [];
      }
      grouped[remark].push(record);
    });
    return grouped;
  }, [filteredRecords]);

  const getCategoryCSVLinks = () => {
    const categoryCSVLinks = {};
    Object.keys(groupedByCategory).forEach((category) => {
      categoryCSVLinks[category] = false;
    });
    return categoryCSVLinks;
  };

  const handleCategoryCheckboxChange = (category) => {
    setSelectedCategoryCheckboxes((prevState) => ({
      ...prevState,
      [category]: !prevState[category],
    }));
  };

  const getClaimNoCSVLinks = () => {
    const claimNoCSVLinks = {};
    Object.keys(groupedByClaimNo).forEach((claimNo) => {
      claimNoCSVLinks[claimNo] = false;
    });
    return claimNoCSVLinks;
  };

  const handleClaimNoCheckboxChange = (claimNo) => {
    setSelectedClaimNoCheckboxes((prevState) => ({
      ...prevState,
      [claimNo]: !prevState[claimNo],
    }));
  };

  const getLrNoCSVLinks = () => {
    const lrNoCSVLinks = {};
    Object.keys(groupedByLrNo).forEach((lrNo) => {
      lrNoCSVLinks[lrNo] = false;
    });
    return lrNoCSVLinks;
  };

  const handleLrNoCheckboxChange = (lrNo) => {
    setSelectedLrNoCheckboxes((prevState) => ({
      ...prevState,
      [lrNo]: !prevState[lrNo],
    }));
  };

  const getRemarkCSVLinks = () => {
    const remarkCSVLinks = {};
    Object.keys(groupedByRemark).forEach((remark) => {
      remarkCSVLinks[remark] = false;
    });
    return remarkCSVLinks;
  };


  const handleRemarkCheckboxChange = (remark) => {
    setSelectedRemarkCheckboxes((prevState) => ({
      ...prevState,
      [remark]: !prevState[remark],
    }));
  };

  const formatRecords = (record, headers) => {
    const formattedRecord = {};
    headers.forEach((header) => {
      const keys = header.key.split('.');
      let value = record;
      for (const key of keys) {
        value = value ? value[key] : '';
      }
      formattedRecord[header.label] = value;
    });
    return formattedRecord;
  };

  const handleXlsxDownload = () => {
    if (filteredRecords.length === 0) {
      toast.error('No records to export.');
      return;
    }
    const xlsxData = filteredRecords.map((record) => formatRecords(record, headers));
    const ws = XLSX.utils.json_to_sheet(xlsxData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${fileName}.xlsx`);
  };

  const handleSAPDownload = async () => {
    if (sessionStorage.getItem('reportType') !== 'fdc-online') return;

    if (filteredRecords.length === 0) {
      toast.error('No records available for SAP file generation.');
      return;
    }

    const groupedRecordsByClaimNo = {};
    filteredRecords.forEach((record) => {
      const claimNo = record?.claimNo || 'Uncategorized';
      if (!groupedRecordsByClaimNo[claimNo]) {
        groupedRecordsByClaimNo[claimNo] = [];
      }
      groupedRecordsByClaimNo[claimNo].push(record);
    });

    const zip = new JSZip();
    let hasFiles = false;

    const sortedClaimNos = Object.keys(groupedRecordsByClaimNo).sort((a, b) => a.localeCompare(b));

    for (const claimNo of sortedClaimNos) {
      const claimRecords = groupedRecordsByClaimNo[claimNo];

      if (claimRecords.length === 0) continue;

      hasFiles = true;

      const fdclrno = claimRecords[0]?.lrNo || '';
      const fdcclaimno = claimRecords[0]?.claimNo || '';
      const fdclocation = claimRecords[0]?.associateDetails?.address || '';
      const fdcclientName = claimRecords[0]?.associateDetails?.name || '';
      const fdcLrDate = moment(claimRecords[0]?.lrDate, 'DD/MM/YYYY').format('DD/MM/YYYY') || '';
      const fdcrecDate = moment(claimRecords[0]?.claimDate, 'DD/MM/YYYY HH:mm:ss').format('DD/MM/YYYY') || '';

      const firstrowfdc = ['Name', fdcclientName, 'LR.NO', fdclrno, 'L.R. DT.', fdcLrDate];
      const secondrowfdc = ['LOCATION', fdclocation, 'CL.NO', fdcclaimno, 'CLAIM DT.', fdcrecDate];
      const csvHeaders = ['SR.NO', 'PRODUCT NAME', 'SAP CODE', 'QTY.', 'BATCH', 'STORAGE'];

      const rows = claimRecords.map((record, index) => [
        index + 1,
        record.product.SKU,
        record.product.ITEM_CODE || '',
        record.quantity,
        record.product.BATCHNUMBER,
        record.product.category,
      ]);

      const data = [firstrowfdc, secondrowfdc, csvHeaders, ...rows];

      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws);

      const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

      const safeClaimNo = claimNo.replace(/[^a-zA-Z0-9_-]/g, '_');

      zip.file(`${safeClaimNo}.xlsx`, buffer);
    }

    if (hasFiles) {
      try {
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, `${fileName}_SAP_claims.zip`);
      } catch (error) {
        toast.error('Error generating SAP ZIP. Please try again.');
      }
    } else {
      toast.error('No records available for SAP file generation.');
    }
  };

  const handleDownloadZip = async () => {
    if (filteredRecords.length === 0) {
      toast.error('No records to export.');
      return;
    }
    const zip = new JSZip();
    const xlsxHeaders = headers.map((header) => header.label);

    const groupedRecordsByClaimNo = {};
    filteredRecords.forEach((record) => {
      const claimNo = record?.claimNo || 'Uncategorized';
      if (!groupedRecordsByClaimNo[claimNo]) {
        groupedRecordsByClaimNo[claimNo] = [];
      }
      groupedRecordsByClaimNo[claimNo].push(record);
    });

    const claimNos = Object.keys(groupedRecordsByClaimNo).sort((a, b) => a.localeCompare(b));

    if (claimNos.length === 0) {
      toast.error('No claim numbers found.');
      return;
    }

    let hasFiles = false;

    for (const claimNo of claimNos) {
      const recordsForClaim = groupedRecordsByClaimNo[claimNo];
      if (!recordsForClaim || recordsForClaim.length === 0) continue;

      const xlsxData = recordsForClaim.map((record) => formatRecords(record, headers));
      const ws = XLSX.utils.json_to_sheet(xlsxData, { header: xlsxHeaders });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Claim');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

      const safeClaimNo = claimNo.replace(/[^a-zA-Z0-9_-]/g, '_') || 'UnknownClaim';
      zip.file(`claims/${safeClaimNo}.xlsx`, excelBuffer);
      hasFiles = true;
    }

    const allData = filteredRecords.map((record) => formatRecords(record, headers));
    const wsAll = XLSX.utils.json_to_sheet(allData, { header: xlsxHeaders });
    const wbAll = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wbAll, wsAll, 'All Data');
    const allExcelBuffer = XLSX.write(wbAll, { bookType: 'xlsx', type: 'array' });
    zip.file('All_Data.xlsx', allExcelBuffer);

    if (hasFiles) {
      try {
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, `${fileName}_claims_export.zip`);
      } catch (error) {
        console.error('ZIP generation error:', error);
        toast.error('Error generating ZIP file. Please try again.');
      }
    } else {
      toast.error('No valid claim records found for download.');
    }
  };

  const handleDownloadSelectedCategoriesText = (selectedCategoryCheckboxes) => {
    if (sessionStorage.getItem('reportType') !== 'fdc-online') return;

    const zip = new JSZip();

    Object.keys(selectedCategoryCheckboxes).sort((a, b) => a.localeCompare(b)).forEach((category) => {
      if (selectedCategoryCheckboxes[category]) {
        let xlsxData = [];
        groupedByCategory[category].forEach((record, index) => {
          xlsxData.push({
            'SR.NO': index + 1,
            'Product Name': record.product.SKU,
            'Item Code': record.product.ITEM_CODE || '',
            'Quantities': record.quantity,
            'Batch Number': record.product.BATCHNUMBER,
            'Name': record.product.category,
          });
        });

        const fdclrno = records[0]?.lrNo || '';
        const fdcclaimno = records[0]?.claimNo || '';
        const fdclocation = records[0]?.associateDetails?.address || '';
        const fdcclientName = records[0]?.associateDetails?.name || '';
        const fdcLrDate = moment(records[0]?.lrDate, 'DD/MM/YYYY').format('DD/MM/YYYY') || '';
        const fdcrecDate = moment(records[0]?.claimDate, 'DD/MM/YYYY HH:mm:ss').format('DD/MM/YYYY') || '';

        const firstrowfdc = ['Name', fdcclientName, 'LR.NO', fdclrno, 'L.R. DT.', fdcLrDate];
        const secondrowfdc = ['LOCATION', fdclocation, 'CL.NO', fdcclaimno, 'CLAIM DT.', fdcrecDate];
        const xlsxHeaders = ['SR.NO', 'PRODUCT NAME', 'SAP CODE', 'QTY.', 'BATCH', 'STORAGE'];

        const updatedXlsxData = xlsxData.map((ele) => ({
          ...ele,
          Name:
            ele['Name'] === 'FG02'
              ? 'FG02'
              : ele['Name'] === 'NEXP'
                ? 'NEXP'
                : 'DAMG',
        }));

        const formattedData = updatedXlsxData.map((record, index) => [
          index + 1,
          record['Product Name'],
          record['Item Code'],
          record['Quantities'],
          record['Batch Number'],
          record['Name'],
        ]);

        const data = [firstrowfdc, secondrowfdc, xlsxHeaders, ...formattedData];

        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws);
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        zip.file(`${category}_report.xlsx`, excelBuffer);
      }
    });

    if (Object.keys(zip.files).length > 0) {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        saveAs(content, `${fileName}_categories.zip`);
      });
    } else {
      toast.error('No categories selected for Excel download.');
    }
  };

  return (
    <div>
      <Typography variant="h5" style={{ textAlign: 'center', marginBottom: 20 }}>
        Download Report
      </Typography>

      <div className="container">
        {loading && (
          <LoadingScreen loading={loading} message={'Loading report, please wait..'} />
        )}
        <div className="row">
          <div className="col">
            <div className="mb-3">
              <label htmlFor="">From Date</label>
              <input
                type="date"
                defaultValue={moment().format('YYYY-MM-DD')}
                onChange={(e) => {
                  setFromDate(moment(e.target.value, 'YYYY-MM-DD').startOf('day'));
                }}
                className="form-control"
              />
            </div>
          </div>
          <div className="col">
            <div className="mb-3">
              <label htmlFor="">To Date</label>
              <input
                type="date"
                defaultValue={moment().format('YYYY-MM-DD')}
                onChange={(e) => {
                  setToDate(moment(e.target.value, 'YYYY-MM-DD').endOf('day'));
                }}
                className="form-control"
              />
            </div>
          </div>
          <div className="col d-flex align-items-end pb-3">
            <button className="btn btn-success" onClick={loadTodaysReport}>
              Create Report
            </button>
          </div>
        </div>

        {loading && <div>Loading report, please wait..</div>}
        {!loading && stockists.length !== 0 && (
          <p className="font-weight-bold">Report data available</p>
        )}
        {!loading && stockists.length === 0 && (
          <p className="font-weight-bold text-danger">Report data not yet available</p>
        )}
      </div>

      <hr />

      {stockists.length > 0 && (
        <>
          <div style={{ display: 'flex', marginTop: -10, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: -10 }}>
              <span style={{ margin: '10px', display: 'flex', alignItems: 'center' }}>
                Category :
              </span>
              {Object.keys(selectedCategoryCheckboxes || {}).sort((a, b) => a.localeCompare(b)).map((category) => (
                <div key={category} style={{ margin: '10px', display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={selectedCategoryCheckboxes[category]}
                    onChange={() => handleCategoryCheckboxChange(category)}
                  />
                  <Typography style={{ marginLeft: '5px' }}>{category}</Typography>
                </div>
              ))}
            </div>
            <div style={{ margin: '10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: 600 }}>
              <span className="input-group-text" id="basic-addon1">
                Stockist
              </span>
              <select
                className="form-control"
                style={{ marginLeft: '10px' }}
                onChange={(e) => onSelectStockist(e.target.value)}
              >
                {stockists.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>
              {(filteredRecordsCount > 0 &&
                (Object.values(selectedCategoryCheckboxes || {}).some(Boolean) ||
                  Object.values(selectedLrNoCheckboxes || {}).some(Boolean) ||
                  Object.values(selectedClaimNoCheckboxes || {}).some(Boolean) ||
                  Object.values(selectedRemarkCheckboxes || {}).some(Boolean))) ? (
                <button
                  className="btn btn-primary float-end"
                  style={{ marginLeft: '10px' }}
                  onClick={handleDownloadZip}
                >
                  Download
                </button>
              ) : (
                <button
                  className="btn btn-primary float-end"
                  style={{ marginLeft: '10px' }}
                  onClick={handleDownloadZip}
                >
                  Download
                </button>
              )}
              {sessionStorage.getItem('reportType') === 'fdc-online' && (
                <button
                  style={{ width: 300, marginLeft: 10 }}
                  className="btn btn-primary float-end"
                  onClick={handleSAPDownload}
                >
                  SAP FILE
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', marginTop: -10, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: -10 }}>
              <span style={{ margin: '10px', display: 'flex', alignItems: 'center' }}>
                LR No. :
              </span>
              {Object.keys(selectedLrNoCheckboxes || {}).sort((a, b) => a.localeCompare(b)).map((lrNo) => (
                <div key={lrNo} style={{ margin: '10px', display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={selectedLrNoCheckboxes[lrNo]}
                    onChange={() => handleLrNoCheckboxChange(lrNo)}
                  />
                  <Typography style={{ marginLeft: '5px' }}>{lrNo}</Typography>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', marginTop: -10, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: -10 }}>
              <span style={{ margin: '10px', display: 'flex', alignItems: 'center' }}>
                Claim No. :
              </span>
              {Object.keys(selectedClaimNoCheckboxes || {}).sort((a, b) => a.localeCompare(b)).map((claimNo) => (
                <div key={claimNo} style={{ margin: '10px', display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={selectedClaimNoCheckboxes[claimNo]}
                    onChange={() => handleClaimNoCheckboxChange(claimNo)}
                  />
                  <Typography style={{ marginLeft: '5px' }}>{claimNo}</Typography>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', marginTop: -10, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: -10 }}>
              <span style={{ margin: '10px', display: 'flex', alignItems: 'center' }}>
                Remark :
              </span>
              {Object.keys(selectedRemarkCheckboxes || {}).sort((a, b) => a.localeCompare(b)).map((remark) => (
                <div key={remark} style={{ margin: '10px', display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={selectedRemarkCheckboxes[remark]}
                    onChange={() => handleRemarkCheckboxChange(remark)}
                  />
                  <Typography style={{ marginLeft: '5px' }}>{remark}</Typography>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <Divider style={{ marginBottom: 20 }} />

      {filteredRecordsCount > 0 && stockists.length > 0 && (
        <table className="table table-striped" style={{ minWidth: 1000 }}>
          <thead>
            <tr>
              <th>#</th>
              <th>Type</th>
              <th>Product Name</th>
              <th>Batch Number</th>
              <th>MRP</th>
              <th>Quantity</th>
              <th>Expiry Date</th>
              <th>Mfg Date</th>
              <th>Category</th>
              <th>Claim No.</th>
              <th>LR No.</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item?.type}</td>
                <td>{item?.product?.SKU}</td>
                <td>{item?.product?.BATCHNUMBER}</td>
                <td>{parseFloat(item?.product?.MRP) || 0}</td>
                <td>{item?.quantity}</td>
                <td>{item?.formattedExpiryDate}</td>
                <td>{item?.formattedMFGDate}</td>
                <td>{item?.product?.category}</td>
                <td>{item?.claimNo}</td>
                <td>{item?.lrNo}</td>
                <td>{item?.product?.remark}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ExportReports;